// schema
import * as ISchema from "@schema";

// libraries
import React, { useState, useEffect, useMemo } from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import { createRefetchContainer } from "react-relay";
import { Button, Divider, FormControl, MenuItem, Select, Tooltip } from "@material-ui/core";
import {
  Check,
  CheckCircle,
  HelpOutline,
  RadioButtonUnchecked,
  NotInterested,
  Warning,
} from "@material-ui/icons";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Address } from "@stripe/stripe-js";

// components
import { PaymentConfirmation } from "../payment-confirmation";
import { LocalizedAddressForm } from "../localized-address-form";
import { OrgUserSelectForm } from "@components/org-user-select-form";
import { CalloutBox } from "@components/callout-box";
import { AvatarRow } from "@components/avatar-row";
import { TeamJoinCreateDialog } from "@components/team-join-create-dialog";
import { LargeControlGroup } from "@clockwise/web-commons/src/components/large-control-group";
// import { CountryPicker } from '@components/country-picker';
import { H1, H2, A } from "@ui";
import { ProBadge } from "@clockwise/web-commons/src/ui/pro-badge";

// util
import {
  isSubscriptionProductTypePro,
  isTeamInPaidSubscription,
  generateStringFromStripeErrorCode,
} from "@util/pricing.util";
import { getOrgBillingGroup, getSubscriptionFromBillingGroup } from "@util/billing-group.util";
import { maybeGetPrimaryTeamFromOrg, createTeamNameFromUserEmail } from "@util/team.util";
import { getDomainOffEmail } from "@clockwise/client-commons/src/util/email";
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { getBulkExperiments } from "@util/experiment.util";
import { getArrayOfNumbersDivisbleByN } from "@util/math.util";

// constants
import { proOrgTier } from "@constants/org-pricing.constant";
import { paths, supportEmail, salesEmail, salesContactFormUrl } from "@constants/site.constant";

// state
// import { IReduxState } from '@reducers/root.reducer';

// styles
import { greys, colors } from "@clockwise/web-commons/src/styles/color.styles";
import { fontFamilySans } from "@clockwise/web-commons/src/styles/type.styles";

// mutations
import {
  createOrgSubscriptionStripe,
  createTeamSubscriptionStripe,
  createTeamAndTeamSubscriptionStripe,
  deleteTeam,
} from "@mutations";

// internal
import { IContainer, IProps, CheckoutFormRoute, ISeatPlanData } from "./MixedCheckoutForm.d";
import { styles } from "./MixedCheckoutForm.styles";
import { mixedCheckoutFormFragments, query } from "./MixedCheckoutForm.gql";

const MAX_ORG_SEAT_SIZE = 150;
const MAX_TEAM_SEAT_SIZE = 50;
/// vv Disable until reduced seat size experiment is over vv
// const MIN_SEAT_SIZE = 5;
const MAX_ORG_SIZE_DEFAULT_TEAM = 200;

const stripeInputStylingBase = {
  style: {
    base: {
      color: greys.slate,
      fontFamily: fontFamilySans,
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: greys.lightishGrey,
      },
    },
    invalid: {
      color: colors.red.standard,
      iconColor: colors.red.standard,
    },
  },
};

const MixedCheckoutFormComponent = (props: IProps) => {
  const ORG_PRICE_YEARLY = proOrgTier.planPricing.yearlyCost;
  const ORG_PRICE_MONTHLY = proOrgTier.planPricing.monthlyCost;

  const { org } = props;

  // ~-~-~-~-~-~-~-
  // Init hooks
  // ~-~-~-~-~-~-~-
  const stripe = useStripe();
  const elements = useElements();

  const experiments = useMemo(() => {
    return getBulkExperiments(org);
  }, [org]);

  useEffect(() => {
    if (experiments.CheckoutPageTeamsDefault) {
      track(TrackingEvents.CHECKOUT.EXPERIMENT_CHECKOUT_PAGE_TEAMS_DEFAULT);
    }
  }, []);

  const [seatOptions, setSeatOptions] = useState([
    {
      seatType: "Org",
      on: true,
    },
    {
      seatType: "Team",
      on: false,
      disabled: !!props.team && !!isTeamInPaidSubscription(props.org, props.team),
    },
  ]);

  // ~-~-~-~-~-~-~-
  // Redux
  // ~-~-~-~-~-~-~-
  // const reduxState = useSelector<IReduxState, IPaymentsState>(state => state.payments);
  // const clearPaymentInfo = () => dispatch(clearPaymentInfoAction());

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const getCurrentView = () => {
    const orgBg = props.org && getOrgBillingGroup(props.org);
    const orgBgSubscription = orgBg && getSubscriptionFromBillingGroup(orgBg);

    if (orgBgSubscription && isSubscriptionProductTypePro(orgBgSubscription.subscription)) {
      return CheckoutFormRoute.PaymentConfirmation;
    }

    return CheckoutFormRoute.Main;
  };

  const isTeamPlanSelected = () => {
    const teamSeatType = seatOptions.find((option) => option.seatType === "Team");
    return teamSeatType && teamSeatType.on;
  };

  const onOrgUserSelectConfirm = (
    newSelectedUserIds: { userId: string; isSuggested?: boolean }[],
  ) => {
    setSelectedUserIds(newSelectedUserIds);
  };

  const getSeatCount = () => {
    const { team } = props;

    if (isTeamPlanSelected()) {
      return (team && team.teamMembers && (team.teamMembers || []).length) || 0;
    }

    return selectedUserIds.length;
  };

  const getCurrentOrgPersons = (org: ISchema.IOrg) => {
    if (org.currentOrgPersons.__typename === "GraphEntityError") {
      logger.error("failed to render curent users from getCurrentOrgPersons");
      return [];
    }
    return org.currentOrgPersons.list;
  };

  const minimumSeatCount = experiments.ReduceMinSeatCountTo1 && isTeamPlanSelected() ? 1 : 5;

  const shouldFormSubmitBeDisabled = () => {
    let isTeamSubscriptionValid = !!(
      isTeamPlanSelected() &&
      (!props.team || isTeamInPaidSubscription(props.org, props.team))
    );
    if (experiments.ReduceMinSeatCountTo1) {
      isTeamSubscriptionValid = !!(
        isTeamPlanSelected() &&
        props.team &&
        !!isTeamInPaidSubscription(props.org, props.team)
      );
    }
    return paying || paidSeatCount < minimumSeatCount || isTeamSubscriptionValid;
  };

  // ~-~-~-~-~-~-~-
  // State
  // ~-~-~-~-~-~-~-
  const [route, setRoute] = useState(getCurrentView());

  const [checkoutError, setCheckoutError] = useState<string>();
  const [stripeError, setStripeError] = useState<string>();
  const [paying, setPaying] = useState(false);

  const [selectedBillingRecurrence, setSelectedBillingRecurrence] = useState(
    props.billingRecurrence ? props.billingRecurrence : ISchema.SubscriptionInterval.Yearly,
  );

  const defaultUserIds = getCurrentOrgPersons(props.org).map((aop) => ({ userId: aop.userId }));
  const [selectedUserIds, setSelectedUserIds] = useState<
    { userId: string; isSuggested?: boolean }[]
  >(defaultUserIds);

  const numSeats = getSeatCount();
  const [paidSeatCount, setPaidSeatCount] = useState(
    numSeats >= minimumSeatCount ? numSeats : minimumSeatCount,
  );

  const [expandSeatForm, setExpandSeatForm] = useState(true);

  const [billingAddress, setBillingAddress] = useState<Address>({
    city: "", // City, district, suburb, town, or village.
    country: "US", // 2-letter country code
    line1: "", // Address line 1 (e.g., street, PO Box, or company name).
    line2: "", // Address line 2 (e.g., apartment, suite, unit, or building).
    postal_code: "", // ZIP or postal code.
    state: "", // State, county, province, or region.
  });

  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [teamModalAction, setTeamModalAction] = useState<"select" | "join">("join");

  const [orgUserSelectModal, setOrgUserSelectModal] = useState(false);

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  useEffect(() => {
    if (getSeatCount() > MAX_ORG_SEAT_SIZE) {
      track(TrackingEvents.CHECKOUT.PAID_SEAT_COUNT_ABOVE_THRESHOLD);
    }
    const shouldTeamBePreselectedByDefault =
      experiments.ReduceMinSeatCountTo1 &&
      org.currentOrgPersons.__typename === "OrgPersonList" &&
      org.currentOrgPersons.list.length > 200;
    // Set Team Selection as default for organizations with over 200 persons
    if (shouldTeamBePreselectedByDefault) {
      const newSeatOptions = [...seatOptions];
      newSeatOptions[0].on = false;
      newSeatOptions[1].on = true;
      setSeatOptions(newSeatOptions);
    }
  }, [org]);

  useEffect(() => {
    // set how many seats have been selected based on new team
    const seatCount = getSeatCount();
    const currentOrgPersonsCount = getCurrentOrgPersons(org).length;
    setPaidSeatCount(seatCount >= minimumSeatCount ? seatCount : minimumSeatCount);

    // decide whether newly fetched team is disabled or not
    const newSeatOptions = [...seatOptions];
    newSeatOptions[1].disabled = !!props.team && !!isTeamInPaidSubscription(props.org, props.team);

    // Check if experiment is on and meets conditions to default to team
    if (
      experiments.CheckoutPageTeamsDefault &&
      currentOrgPersonsCount > MAX_ORG_SIZE_DEFAULT_TEAM
    ) {
      newSeatOptions[0].on = false;
      newSeatOptions[1].on = true;
    }

    setSeatOptions(newSeatOptions);
  }, [props.team]);

  const currentUserInfo = useMemo(() => {
    const currentOrgPersons = getCurrentOrgPersons(org);
    const currentUser = currentOrgPersons.filter((person: ISchema.IOrgPerson) => person.isYou)[0];
    if (currentUser.personId && currentUser.primaryCalendarId) {
      return currentUser;
    } else {
      return null;
    }
  }, [props.org]);

  const preSavedTeamName = useMemo(() => {
    if (!currentUserInfo || !currentUserInfo?.primaryCalendarId) {
      return null;
    }
    return createTeamNameFromUserEmail(currentUserInfo.primaryCalendarId);
  }, [currentUserInfo]);

  // useEffect(() => {
  //   setSelectedBillingRecurrence(props.billingRecurrence ? props.billingRecurrence : ISchema.SubscriptionInterval.Yearly,);
  // }, [props.billingRecurrence]);

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  const onChangeBillingRecurrence = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const billingRecurrence = event.target.value as ISchema.SubscriptionInterval;

    setSelectedBillingRecurrence(billingRecurrence);

    track(TrackingEvents.CHECKOUT.SELECT_BILLING_RECURRENCE, { billingRecurrence });

    if (props.onChangeBillingRecurrence) {
      props.onChangeBillingRecurrence(billingRecurrence);
    }
  };

  const onChangePaidSeatCount = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newPaidSeatCount = (event.target.value as unknown) as number;

    setPaidSeatCount(newPaidSeatCount);

    // track(TrackingEvents.CHECKOUT.SELECT_BILLING_RECURRENCE, { billingRecurrence });
  };

  const onChangeSeatPlanType = (_index: number, control: ISeatPlanData) => {
    if (!control.on) {
      return;
    }

    control.seatType === "Org" ? onSelectOrgOption() : onSelectTeamOption();

    const newList = [...seatOptions];
    newList.forEach((br) => {
      if (br.seatType === control.seatType) {
        br.on = true;
      } else {
        br.on = false;
      }
    });

    const minSeat = experiments.ReduceMinSeatCountTo1 && control.seatType === "Team" ? 1 : 5;

    setSeatOptions(newList);
    const seatCount = getSeatCount();
    setPaidSeatCount(seatCount >= minSeat ? seatCount : minSeat);
  };

  const onCloseOrgUserSelectForm = () => {
    setOrgUserSelectModal(false);
    track(TrackingEvents.CHECKOUT.CLOSE_ORG_USER_SELECT_MODAL);
  };

  const onOpenOrgUserSelectForm = () => {
    setOrgUserSelectModal(true);
    track(TrackingEvents.CHECKOUT.OPEN_ORG_USER_SELECT_MODAL);
  };

  const onCloseTeamModal = (teamId?: string) => {
    setTeamModalOpen(false);

    if (teamId) {
      if (props.onChangeTeam) {
        props.onChangeTeam(teamId);
      }

      props.relay.refetch({ orgRelayId: props.org.id }, undefined, undefined, { force: true });
    }

    track(TrackingEvents.CHECKOUT.CLOSE_TEAM_JOIN_CREATE_MODAL, { teamId });
  };

  const onOpenTeamModal = (action: "select" | "join") => {
    setTeamModalOpen(true);
    setTeamModalAction(action);

    track(TrackingEvents.CHECKOUT.OPEN_TEAM_JOIN_CREATE_MODAL);
  };

  const onAddressChange = (address: Address) => {
    setBillingAddress(address);
  };

  // ~-~-~-~-~-~-~-
  // Mutation
  // ~-~-~-~-~-~-~-

  const deleteTeamMadeForCheckout = (teamId: string) => {
    const { relay, org } = props;
    deleteTeam(
      relay.environment,
      { orgRelayId: org.id, teamId: teamId },
      () => {
        // do nothing
      },
      () => {
        // do nothing
      },
    );
  };

  const createSubscription = () => {
    const { team } = props;

    if (team && isTeamPlanSelected()) {
      // only go here if user has selected team
      return createTeamSubscription(team);
    } else if (!team && isTeamPlanSelected()) {
      // only go here if user has selected team AND has no teams
      return createTeamSubscription();
    } else {
      return createOrgSubscription();
    }
  };

  const createOrgSubscription = async () => {
    if (!stripe || !elements) {
      return;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    const cardExpiry = elements.getElement(CardExpiryElement);
    const cardCvc = elements.getElement(CardCvcElement);

    if (!cardNumber || !cardExpiry || !cardCvc) {
      return logger.error("couldnt find stripe elements");
    }

    track(TrackingEvents.CHECKOUT.CLICK_CHECKOUT);
    setPaying(true);
    setCheckoutError(undefined);

    const orgBg = props.org && getOrgBillingGroup(props.org);
    const orgBgSubscription = orgBg && getSubscriptionFromBillingGroup(orgBg);
    if (orgBgSubscription && isSubscriptionProductTypePro(orgBgSubscription.subscription)) {
      logger.error("user trying to pay when they have already paid");
      return;
    }

    const stripeResponse = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumber,
      billing_details: {
        address: {
          country: billingAddress.country ? billingAddress.country : undefined,
          line1: billingAddress.line1 ? billingAddress.line1 : undefined,
          line2: billingAddress.line2 ? billingAddress.line2 : undefined,
          postal_code: billingAddress.postal_code ? billingAddress.postal_code : undefined,
          state: billingAddress.state ? billingAddress.state : undefined,
        },
        email: props.org.primaryOrgEmail,
      },
    });

    setStripeError(generateStringFromStripeErrorCode(stripeResponse.error));

    if (stripeResponse.error) {
      setPaying(false);
      return logger.error(`stripe error with error code: ${stripeResponse.error.code}`);
    } else if (stripeResponse.paymentMethod) {
      createOrgSubscriptionStripe(
        props.relay.environment,
        {
          orgRelayId: props.org.id,
          stripePaymentMethodId: stripeResponse.paymentMethod.id,
          initialUsers: selectedUserIds.map((u) => u.userId),
          recurrence: selectedBillingRecurrence as any, // HACK: i need to refactor this to all be ISchema.CreateOrgSubscriptionStripeRecurrenceEnum
          numSeats: paidSeatCount,
        },
        (s: ISchema.ICreateOrgSubscriptionStripePayload) => {
          setPaying(false);
          if (s.stripeError) {
            setStripeError(generateStringFromStripeErrorCode(s.stripeError));
            return logger.error(
              `failed to createOrgSubscriptionStripe because of stripe error: ${s.stripeError.code}`,
            );
          }
          setRoute(CheckoutFormRoute.PaymentConfirmation);
        },
        () => {
          setPaying(false);
          logger.error("failed to createOrgSubscriptionStripe");
          setCheckoutError(`Failed to upgrade. Please try again.`);
        },
      );
    }
  };

  const createTeamSubscription = async (team?: ISchema.ITeam) => {
    if (!team && !experiments.ReduceMinSeatCountTo1) {
      //control state does not allow for team subscriptions without a premade team
      logger.error("Team must exist for team subscription to be created");
      setCheckoutError(`Team Subscription requires a team to be created/joined`);
      return;
    }
    if (!stripe || !elements) {
      return;
    }
    const cardNumber = elements.getElement(CardNumberElement);
    const cardExpiry = elements.getElement(CardExpiryElement);
    const cardCvc = elements.getElement(CardCvcElement);

    if (!cardNumber || !cardExpiry || !cardCvc) {
      return logger.error("couldnt find stripe elements");
    }

    if (team) {
      track(TrackingEvents.CHECKOUT.CLICK_CHECKOUT, { teamId: team.teamId });
    }
    setPaying(true);
    setCheckoutError(undefined);

    if (team && isTeamInPaidSubscription(props.org, team)) {
      return logger.error("user trying to pay when they have already paid");
    }

    const stripeResponse = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumber,
      billing_details: {
        address: {
          country: billingAddress.country ? billingAddress.country : undefined,
          line1: billingAddress.line1 ? billingAddress.line1 : undefined,
          line2: billingAddress.line2 ? billingAddress.line2 : undefined,
          postal_code: billingAddress.postal_code ? billingAddress.postal_code : undefined,
          state: billingAddress.state ? billingAddress.state : undefined,
        },
        email: props.org.primaryOrgEmail,
      },
    });

    setStripeError(generateStringFromStripeErrorCode(stripeResponse.error));

    if (stripeResponse.error) {
      setPaying(false);
      return logger.error(`stripe error with error code: ${stripeResponse.error.code}`);
    } else if (stripeResponse.paymentMethod) {
      // Create team here if we still dont have one
      if (!team) {
        createTeamAndTeamSubscriptionStripe(
          props.relay.environment,
          {
            orgRelayId: props.org.id,
            subscriptionInterval: selectedBillingRecurrence,
            stripePaymentMethodId: stripeResponse.paymentMethod.id,
            seatCount: paidSeatCount,
            userEmail: currentUserInfo?.primaryCalendarId || "",
            personId: currentUserInfo?.personId || "",
          },
          (s: ISchema.ICreateTeamAndTeamSubscriptionStripePayload) => {
            if (s.stripeError) {
              deleteTeamMadeForCheckout(s.team.teamId);
              setStripeError(generateStringFromStripeErrorCode(s.stripeError));
              logger.error(
                `failed to createTeamSubscriptionStripe because of stripe error: ${s.stripeError.code}`,
              );
              setPaying(false);
              return;
            }
            setPaying(false);
            setRoute(CheckoutFormRoute.PaymentConfirmation);
          },
          (err) => {
            if (
              err.message.includes("TEAM ALREADY EXISTS") ||
              err.message.includes("Failed to create Team")
            ) {
              setCheckoutError(
                `Team creation failed. Please try creating a team by selecting "Create New Team"`,
              );
            } else {
              setCheckoutError(`Failed to upgrade. Please try again.`);
            }
            setPaying(false);
            logger.error("failed to createTeamAndTeamSubscriptionStripe");
          },
        );
      } else {
        createTeamSubscriptionStripe(
          props.relay.environment,
          {
            orgRelayId: props.org.id,
            teamId: team.teamId,
            subscriptionInterval: selectedBillingRecurrence,
            stripePaymentMethodId: stripeResponse.paymentMethod.id,
            seatCount: paidSeatCount,
          },
          (s: ISchema.ICreateTeamSubscriptionStripePayload) => {
            setPaying(false);
            if (s.stripeError) {
              setStripeError(generateStringFromStripeErrorCode(s.stripeError));
              logger.error(
                `failed to createTeamSubscriptionStripe because of stripe error: ${s.stripeError.code}`,
              );
              return;
            }
            setRoute(CheckoutFormRoute.PaymentConfirmation);
          },
          () => {
            setPaying(false);
            logger.error("failed to createTeamSubscriptionStripe");
            setCheckoutError(`Failed to upgrade. Please try again.`);
          },
        );
      }
    }
  };

  // ~-~-~-~-~-~-~-
  // Tracking
  // ~-~-~-~-~-~-~-

  const onClickContactSales = () => {
    track(TrackingEvents.CHECKOUT.CONTACT_SALES_CLICKED);
  };

  const onSelectOrgOption = () => {
    track(TrackingEvents.CHECKOUT.ORG_OPTION_SELECTED);
  };

  const onSelectTeamOption = () => {
    track(TrackingEvents.CHECKOUT.TEAM_OPTION_SELECTED);
  };

  const onClickCreateTeamFromOption = () => {
    onOpenTeamModal("join");
    track(TrackingEvents.CHECKOUT.CREATE_TEAM_CLICKED);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  const maybeRenderOrgUserSelectModal = () => {
    const { viewer, org } = props;

    return (
      <div>
        <OrgUserSelectForm
          viewer={viewer}
          org={org}
          showCurrentUser={true}
          onConfirm={onOrgUserSelectConfirm}
          selectedUserIds={selectedUserIds}
          isDialog
          open={orgUserSelectModal}
          onRequestClose={onCloseOrgUserSelectForm}
          viewOnly
        />
      </div>
    );
  };

  const maybeRenderTeamCreateModal = () => {
    if (!teamModalOpen) {
      return "";
    }

    return (
      <TeamJoinCreateDialog
        open={teamModalOpen}
        onClose={onCloseTeamModal}
        org={props.org}
        forcedInitialView={teamModalAction === "select" ? "select" : "create"}
        mode="checkout"
        selectMode={teamModalAction === "select"}
      />
    );
  };

  const renderPaymentConfirmation = () => {
    const { org, isDialog } = props;
    return (
      <PaymentConfirmation
        org={org}
        team={null}
        isDialog={isDialog}
        onChangeTeam={() => console.log("change")}
      />
    );
  };

  const renderControlContent = (control: ISeatPlanData) => {
    const { classes, org, team } = props;

    const maybePrimaryTeam = maybeGetPrimaryTeamFromOrg(props.org);
    const primaryTeamId = maybePrimaryTeam ? maybePrimaryTeam.teamId : "";

    if (team && control.seatType === "Team") {
      return (
        <div className={classes.teamSeats}>
          {control.disabled ? (
            <Tooltip title="This team is already on the Pro plan.">
              <NotInterested />
            </Tooltip>
          ) : control.on ? (
            <CheckCircle />
          ) : (
            <RadioButtonUnchecked />
          )}
          <div>
            <div className={classes.seatFormItemHeader}>
              <div>
                {team.teamName} {primaryTeamId === team.teamId ? "(primary team)" : ""}
              </div>
              {isTeamInPaidSubscription(org, team) && <ProBadge className={classes.badgePro} />}
            </div>
            <div className={classes.seatFormItemSubHead} onClick={() => onOpenTeamModal("select")}>
              Change team
            </div>
          </div>
        </div>
      );
    } else if (control.seatType === "Team") {
      return (
        <div className={classes.teamSeats}>
          {control.disabled ? (
            <NotInterested />
          ) : control.on ? (
            <CheckCircle />
          ) : (
            <RadioButtonUnchecked />
          )}
          <div>
            <div className={classes.seatFormItemHeader}>
              {experiments.ReduceMinSeatCountTo1 ? (
                <div>{preSavedTeamName ? `${preSavedTeamName}` : "Your team"}</div>
              ) : (
                <div>Your team</div>
              )}
            </div>
            <div className={classes.seatFormItemSubHead} onClick={onClickCreateTeamFromOption}>
              Create new team
            </div>
          </div>
        </div>
      );
    } else if (control.seatType === "Org") {
      const currentOrgPersonsCount = getCurrentOrgPersons(org).length;
      return (
        <div className={classes.orgSeats}>
          {control.disabled ? (
            <NotInterested />
          ) : control.on ? (
            <CheckCircle />
          ) : (
            <RadioButtonUnchecked />
          )}
          <div>
            <div className={classes.seatFormItemHeader}>
              Your organization ({getDomainOffEmail(org.primaryOrgCalendar)})
            </div>
            <div className={classes.seatFormItemSubHead} onClick={onOpenOrgUserSelectForm}>
              {currentOrgPersonsCount} current user{currentOrgPersonsCount === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderSeatsForm = () => {
    const { classes, org } = props;

    const MAX_RENDERED_AVATARS = 8;
    let teammates: ISchema.IOrgPerson[] = [];
    let extraTeammatesCount = 0;

    const currentOrgPersons = getCurrentOrgPersons(org);

    teammates = [...currentOrgPersons].slice(0, MAX_RENDERED_AVATARS);

    extraTeammatesCount =
      currentOrgPersons.length > MAX_RENDERED_AVATARS
        ? currentOrgPersons.length - MAX_RENDERED_AVATARS
        : 0;

    const showFullForm = expandSeatForm || isTeamPlanSelected();

    const currentOrgPersonsCount = getCurrentOrgPersons(org).length;

    if (!showFullForm) {
      return (
        <div className={classes.seatForm}>
          <div className={classes.orgSeatsView}>
            <div className={classes.seatFormItemHeader}>
              <div>Upgrade organization ({getDomainOffEmail(org.primaryOrgCalendar)})</div>
              <div className={classes.seatFormItemCta}>
                <A
                  onClick={() => {
                    setExpandSeatForm(true);
                    track(TrackingEvents.CHECKOUT.CLICK_MEMBER_PLAN_TYPE_EDIT);
                  }}
                >
                  Upgrade a team
                </A>
              </div>
            </div>
            <div className={classes.seatFormItemSubHead} onClick={onOpenOrgUserSelectForm}>
              {currentOrgPersonsCount} current user{currentOrgPersonsCount === 1 ? "" : "s"}
            </div>
            <AvatarRow
              orgPersons={teammates}
              maxAvatarCount={MAX_RENDERED_AVATARS}
              size="medium"
              extraOverflowCount={extraTeammatesCount}
              showTooltip={true}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={classes.seatForm}>
        <LargeControlGroup
          controls={seatOptions}
          renderControlContent={renderControlContent}
          onChange={onChangeSeatPlanType}
          allowPointerEvents
          overrideContent
        />
      </div>
    );
  };

  const renderCheckoutForm = () => {
    const { classes, org } = props;

    return (
      <div className={classes.checkoutForm}>
        <div className={classes.pricingRecurrence}>
          <H2 style={{ marginBottom: 20 }}>You're upgrading</H2>
          {renderSeatsForm()}
          <H2 style={{ marginBottom: 20 }}> Billing cadence </H2>
          <FormControl className={classes.pricingRecurrenceForm}>
            <Select
              classes={{ outlined: classes.pricingRecurrenceSelect }}
              value={selectedBillingRecurrence}
              onChange={onChangeBillingRecurrence}
              name="Billing recurrence"
              inputProps={{
                name: "Billing recurrence",
                id: "cw-billing-recurrence-select",
              }}
              variant="outlined"
              fullWidth
            >
              <MenuItem value={ISchema.SubscriptionInterval.Yearly}>
                <span>Annual plan</span>{" "}
              </MenuItem>
              <MenuItem value={ISchema.SubscriptionInterval.Monthly}>
                <span>Monthly plan</span>{" "}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className={classes.paymentMethod}>
          <H2 style={{ marginBottom: 20 }}>Payment method</H2>
          <div className="fs-exclude">
            <CardNumberElement
              id="plan-management-card-number"
              options={{
                placeholder: "Card number",
                classes: {
                  base: classes.stripeInput,
                },
                ...stripeInputStylingBase,
              }}
            />
          </div>
          <div className="fs-exclude">
            <CardExpiryElement
              id="plan-management-card-expiry"
              options={{
                placeholder: "MM/YY",
                classes: {
                  base: classes.stripeInput,
                },
                ...stripeInputStylingBase,
              }}
            />
            <CardCvcElement
              id="plan-management-card-cvc"
              options={{
                placeholder: "CVC",
                classes: {
                  base: classes.stripeInput,
                },
                ...stripeInputStylingBase,
              }}
            />
          </div>
        </div>
        <div className={classes.billingAddress}>
          <H2 style={{ marginBottom: 20 }}>Billing details</H2>
          <div className="fs-exclude">
            <LocalizedAddressForm
              org={org}
              onChange={onAddressChange}
              initialBillingAddress={billingAddress}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderMixedCheckoutReceiptItems = () => {
    const { classes } = props;

    const isYearlyPricing = selectedBillingRecurrence === ISchema.SubscriptionInterval.Yearly;
    const pricePerUnit = isYearlyPricing ? ORG_PRICE_YEARLY : ORG_PRICE_MONTHLY;
    const perInterval = isYearlyPricing ? "year" : "month";
    const costPerInterval = pricePerUnit * paidSeatCount * (isYearlyPricing ? 12 : 1);
    const costPerIntervalIfMonthly = ORG_PRICE_MONTHLY * paidSeatCount * 12;
    const monthlyCostIfAnnualLabel =
      paidSeatCount === 1
        ? paidSeatCount + " user x $" + ORG_PRICE_MONTHLY + " x 12 months"
        : paidSeatCount + " users x $" + ORG_PRICE_MONTHLY + " x 12 months";
    const monthlyCostIfMonthlyLabel =
      paidSeatCount === 1
        ? paidSeatCount + " user x $" + pricePerUnit
        : paidSeatCount + " users x $" + pricePerUnit;
    const monthlyCostIfMonthlyPlan =
      "$" + costPerInterval.toFixed(2) + " / " + perInterval + " + tax";

    const MAX_SEAT_SIZE_FOR_SELECTED_PLAN = isTeamPlanSelected()
      ? MAX_TEAM_SEAT_SIZE
      : MAX_ORG_SEAT_SIZE;

    let seatOptionsForSelectedPlan = isTeamPlanSelected()
      ? [...new Set([getSeatCount(), 1, 2, 3, 4, ...getArrayOfNumbersDivisbleByN(5, 50, 5)])]
      : [...new Set([getSeatCount(), ...getArrayOfNumbersDivisbleByN(5, 150, 5)])];

    if (getSeatCount() > MAX_SEAT_SIZE_FOR_SELECTED_PLAN) {
      return (
        <div className={classes.checkoutConfirmation}>
          <H2 style={{ marginBottom: 20 }}>Review and purchase</H2>
          <div className={classes.reviewAndPurchaseContainer}>
            <p className={classes.maxSeatCountExceededMessage}>
              Your organization has more than {MAX_ORG_SEAT_SIZE} users. Please{" "}
              <A target="_blank" href={salesContactFormUrl}>
                {" "}
                contact sales{" "}
              </A>{" "}
              to upgrade your plan to boost your organization’s productivity with:{" "}
            </p>
            <ul className={classes.maxSeatCountExceededList}>
              <li className={classes.bulletPoints}>
                <Check className={classes.bulletCheckmark} /> Unlimited schedule assists to create
                more time in the day
              </li>
              <li className={classes.bulletPoints}>
                <Check className={classes.bulletCheckmark} /> Productivity tools to work smarter,
                together
              </li>
              <li className={classes.bulletPoints}>
                <Check className={classes.bulletCheckmark} /> Advanced calendar control for
                sustainable workdays
              </li>
            </ul>
            <Button
              variant="contained"
              target="_blank"
              color="primary"
              href={salesContactFormUrl}
              style={{ marginTop: "20px" }}
              fullWidth
              onClick={onClickContactSales}
            >
              {" "}
              Contact Sales
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={classes.checkoutConfirmation}>
        <H2 style={{ marginBottom: 20 }}>Review and purchase</H2>
        <div className={classes.reviewAndPurchaseContainer}>
          <div>
            <div className={classes.checkoutItem}>
              <Check />
              <div className={classes.checkoutLabel}>
                Upgrade your {isTeamPlanSelected() ? "team" : "organization"} to the Pro Plan
              </div>
            </div>
            <div className={classes.checkoutItem}>
              <Check />
              <div className={classes.checkoutLabel}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ padding: "4px 0px 7px 0px" }}>Pay today for</div>
                  <FormControl className={classes.paidSeatCountForm}>
                    <Select
                      classes={{
                        select: classes.paidSeatCountUnderline,
                        outlined: classes.paidSeatCountSelect,
                        root: classes.paidSeatCountSelectLabel,
                      }}
                      value={paidSeatCount}
                      onChange={onChangePaidSeatCount}
                      name="Seat count select"
                      inputProps={{
                        name: "Seat count select",
                        id: "cw-seat-count-select",
                      }}
                      variant="standard"
                      disableUnderline
                    >
                      {seatOptionsForSelectedPlan
                        .filter((c) => c >= getSeatCount() && c >= minimumSeatCount)
                        .map((c) => (
                          <MenuItem key={`seat-count-${c}`} value={c}>
                            {c} seats
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </div>
            {!experiments.ReduceMinSeatCountTo1 && numSeats < minimumSeatCount ? (
              <div className={classes.checkoutItem} style={{ marginLeft: 35 }}>
                <div className={classes.minimumSeatCallout}>5-seat minimum</div>
                <Tooltip title="Clockwise plans are designed for teams & organizations">
                  <HelpOutline className={classes.miminumSeatHelpIcon} />
                </Tooltip>
              </div>
            ) : (
              ""
            )}
            <div className={classes.checkoutItem}>
              <Check />
              <div
                className={classes.checkoutLabel}
                style={{ display: "flex", alignItems: "center" }}
              >
                <div>
                  Pay for future users joining your {isTeamPlanSelected() ? "team" : "organization"}
                </div>
              </div>
            </div>
          </div>
          <Divider className={classes.divider} />
          <div className={classes.costTotalContainer}>
            <div className={classes.costTotalHeader}>Total billed today</div>
            {isYearlyPricing ? (
              <div>
                <div className={classes.costTotalLineItem}>
                  <div className={classes.costArithmetic}>{monthlyCostIfAnnualLabel}</div>
                  <div className={classes.costTotal} style={{ textDecoration: "line-through" }}>
                    ${costPerIntervalIfMonthly.toFixed(2)} + tax
                  </div>
                </div>
                <div className={classes.costTotalLineItemAnnual}>
                  <div className={classes.costArithmetic} style={{ color: colors.green.darker }}>
                    13% Annual Discount
                  </div>
                  <div className={classes.costTotal} style={{ color: colors.green.darker }}>
                    ${costPerInterval.toFixed(2)} + tax
                  </div>
                </div>
              </div>
            ) : (
              <div className={classes.costTotalLineItem}>
                <div className={classes.costArithmetic}>{monthlyCostIfMonthlyLabel}</div>
                <div className={classes.costTotal}>{monthlyCostIfMonthlyPlan}</div>
              </div>
            )}
          </div>

          <Divider className={classes.divider} />
          <div className={classes.purchaseAgreement}>
            By clicking “confirm upgrade,” you agree to our{" "}
            <A target="_blank" href={paths.terms}>
              subscriber terms
            </A>
            . Your subscription will automatically renew until you cancel. The subscription fee will
            be automatically updated if you modify the amount of users in your paid team or
            organization. You can make changes or{" "}
            <A target="_blank" href={`mailto:${supportEmail}`}>
              cancel
            </A>{" "}
            the plan at any time.
          </div>
          <Button
            disabled={shouldFormSubmitBeDisabled()}
            style={{ marginTop: 8 }}
            onClick={() => createSubscription()}
            variant="contained"
            color="primary"
            fullWidth
          >
            Confirm upgrade
          </Button>
        </div>
      </div>
    );
  };

  const renderCheckoutFlow = () => {
    const { classes } = props;

    return (
      <div>
        <H1 style={{ fontSize: 30, marginBottom: 30 }}>Upgrade to the Pro plan</H1>
        {stripeError ? (
          <CalloutBox style={styles.errorBox} error>
            <Warning classes={{ root: classes.calloutIcon }} />
            <span>
              {stripeError}. If the issue persists, please contact{" "}
              <A target="_blank" href={`mailto:${salesEmail}`}>
                {salesEmail}
              </A>
            </span>
          </CalloutBox>
        ) : null}
        {checkoutError ? (
          <CalloutBox style={styles.errorBox} error>
            <Warning classes={{ root: classes.calloutIcon }} />
            <span>
              {checkoutError}. If the issue persists, please contact{" "}
              <A target="_blank" href={`mailto:${salesEmail}`}>
                {salesEmail}
              </A>
            </span>
          </CalloutBox>
        ) : null}
        <div className={classes.checkoutFormContent}>
          {renderCheckoutForm()}
          {renderMixedCheckoutReceiptItems()}
          {maybeRenderOrgUserSelectModal()}
          {maybeRenderTeamCreateModal()}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (route) {
      case CheckoutFormRoute.PaymentConfirmation:
        return renderPaymentConfirmation();
      case CheckoutFormRoute.Main:
      default:
        return renderCheckoutFlow();
    }
  };

  return (
    <div className={props.classes.wrapper}>
      <div className={props.classes.container}>{renderContent()}</div>
    </div>
  );
};

/////////////////
// With Styles //
/////////////////

const MixedCheckoutFormWithStyles = withStyles(styles)(MixedCheckoutFormComponent);

///////////
// Relay //
///////////

export const MixedCheckoutForm = createRefetchContainer<IContainer>(
  MixedCheckoutFormWithStyles,
  mixedCheckoutFormFragments,
  query,
);
